/**
 * missileCollisions.ts
 * Cross-system collision pass for the ship's secondary missile weapon.
 *
 * One missile can damage asteroids, enemies, AND the boss — applying
 * splash damage to OTHER live entities within
 * `SHIP_MISSILE_AOE_RADIUS_PX` of the impact point. Living in its own
 * module keeps the existing per-system collision functions
 * (`resolveShipProjectileAsteroidCollisions`,
 * `resolveShipProjectileEnemyCollisions`,
 * `resolveShipProjectileBossCollisions`) untouched and lets the AoE
 * fan-out read all three pools in a single pass.
 *
 * Pipeline per missile:
 *   1. Find the first hazard the missile overlaps (asteroid → enemy → boss).
 *      That hazard takes `SHIP_MISSILE_DIRECT_DAMAGE`.
 *   2. Compute the impact point as the missile's centre at overlap time.
 *   3. Splash: every OTHER live asteroid / enemy / boss whose centre is
 *      within `SHIP_MISSILE_AOE_RADIUS_PX` of the impact takes
 *      `SHIP_MISSILE_AOE_DAMAGE`.
 *   4. Detonate: spawn an explosion VFX at the impact point and trigger
 *      a small camera shake. Mark the missile dead.
 *
 * Missiles that travel past their TTL or off-screen die in
 * `updateShipMissiles` without detonating — they only AoE on contact.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module missileCollisions
 */

import { spaceState } from './spaceState';
import { spawnExplosion } from './explosionVfx';
import { maybeSpawnSpaceLoot } from './spaceLootSystem';
import { triggerSpaceShake } from './cameraShake';
import { VIEWPORT_WIDTH } from '../../constants/gameConstants';
import {
  SHIP_MISSILE_DIRECT_DAMAGE,
  SHIP_MISSILE_AOE_DAMAGE,
  SHIP_MISSILE_AOE_RADIUS_PX,
  SPACE_SHAKE_MAGNITUDE_MISSILE_PX,
  SPACE_SHAKE_DURATION_MISSILE_S,
} from '../../constants/spaceConstants';

const AOE_RADIUS_SQ = SHIP_MISSILE_AOE_RADIUS_PX * SHIP_MISSILE_AOE_RADIUS_PX;

/**
 * Apply `amount` damage to every live entity (asteroid / enemy / boss)
 * within {@link SHIP_MISSILE_AOE_RADIUS_PX} of `(impactX, impactY)`,
 * excluding the direct-hit target (so it is not double-counted).
 */
function applySplash(
  impactX: number,
  impactY: number,
  amount: number,
  excludeAsteroidIdx: number,
  excludeEnemyIdx: number,
  excludeBoss: boolean,
): void {
  const asteroids = spaceState.asteroids;
  for (let i = 0; i < asteroids.length; i++) {
    if (i === excludeAsteroidIdx) continue;
    const a = asteroids[i];
    if (a.dead || !a.destructible) continue;
    const cx = a.x + a.width / 2;
    const cy = a.y + a.height / 2;
    const dx = cx - impactX;
    const dy = cy - impactY;
    if (dx * dx + dy * dy > AOE_RADIUS_SQ) continue;
    const killed = a.takeDamage(amount);
    if (killed) spawnExplosion(cx, cy);
  }

  const enemies = spaceState.enemies;
  for (let i = 0; i < enemies.length; i++) {
    if (i === excludeEnemyIdx) continue;
    const e = enemies[i];
    if (e.dead || !e.active) continue;
    const cx = e.x + e.width / 2;
    const cy = e.y + e.height / 2;
    const dx = cx - impactX;
    const dy = cy - impactY;
    if (dx * dx + dy * dy > AOE_RADIUS_SQ) continue;
    const killed = e.takeDamage(amount, impactX, impactY);
    if (killed) {
      spawnExplosion(cx, cy);
      maybeSpawnSpaceLoot(cx, cy);
    }
  }

  const boss = spaceState.boss;
  if (!excludeBoss && boss !== null && !boss.dead && boss.state !== 'DEFEATED') {
    const cx = boss.x + boss.width / 2;
    const cy = boss.y + boss.height / 2;
    const dx = cx - impactX;
    const dy = cy - impactY;
    if (dx * dx + dy * dy <= AOE_RADIUS_SQ) {
      boss.takeDamage(amount, impactX, impactY);
    }
  }
}

/**
 * Find the first asteroid the missile AABB overlaps. Mirrors the
 * visibility gate used by `resolveShipProjectileAsteroidCollisions`
 * (skip asteroids whose left edge is already past the viewport right).
 *
 * @returns Index into `spaceState.asteroids`, or `-1` if no overlap.
 */
function firstAsteroidOverlap(
  mx: number,
  my: number,
  mw: number,
  mh: number,
  viewportRight: number,
): number {
  const asteroids = spaceState.asteroids;
  const mr = mx + mw;
  const mb = my + mh;
  for (let i = 0; i < asteroids.length; i++) {
    const a = asteroids[i];
    if (a.dead) continue;
    if (a.x >= viewportRight) continue;
    if (mr <= a.x || mx >= a.x + a.width || mb <= a.y || my >= a.y + a.height) continue;
    return i;
  }
  return -1;
}

/**
 * Find the first enemy the missile AABB overlaps.
 *
 * @returns Index into `spaceState.enemies`, or `-1` if no overlap.
 */
function firstEnemyOverlap(
  mx: number,
  my: number,
  mw: number,
  mh: number,
  viewportRight: number,
): number {
  const enemies = spaceState.enemies;
  const mr = mx + mw;
  const mb = my + mh;
  for (let i = 0; i < enemies.length; i++) {
    const e = enemies[i];
    if (e.dead || !e.active) continue;
    if (e.x >= viewportRight) continue;
    if (mr <= e.x || mx >= e.x + e.width || mb <= e.y || my >= e.y + e.height) continue;
    return i;
  }
  return -1;
}

/**
 * Pairwise AABB collision between live ship missiles and every hazard
 * pool (asteroids → enemies → boss). On the first overlap a missile
 * finds, the direct target takes {@link SHIP_MISSILE_DIRECT_DAMAGE}
 * and every other live hazard within
 * {@link SHIP_MISSILE_AOE_RADIUS_PX} of the impact takes
 * {@link SHIP_MISSILE_AOE_DAMAGE}. The missile is consumed
 * (`dead = true`); `updateShipMissiles` reaps it on the next tick.
 *
 * Off-screen missiles (left edge past viewport right) are skipped to
 * mirror the laser-collision visibility gates.
 *
 * Complexity: O(M * (A + E)) for the find pass plus O(A + E) per hit
 * for the splash pass. Caps of 4 × (24 + 12) = 144 AABB tests per tick
 * + 36 distance tests per hit. Well under the 1 ms engine budget.
 */
export function resolveShipMissileCollisions(): void {
  const missiles = spaceState.missiles;
  if (missiles.length === 0) return;
  const viewportRight = spaceState.cameraX + VIEWPORT_WIDTH;

  for (let i = 0; i < missiles.length; i++) {
    const m = missiles[i];
    if (m.dead) continue;
    if (m.x >= viewportRight) continue;

    // ── 1. Find first hit (asteroid → enemy → boss) ──────────────────────
    const asteroidIdx = firstAsteroidOverlap(m.x, m.y, m.width, m.height, viewportRight);
    let enemyIdx = -1;
    let bossHit = false;
    if (asteroidIdx === -1) {
      enemyIdx = firstEnemyOverlap(m.x, m.y, m.width, m.height, viewportRight);
      if (enemyIdx === -1) {
        const boss = spaceState.boss;
        if (
          boss !== null &&
          !boss.dead &&
          boss.state !== 'DEFEATED' &&
          boss.x < viewportRight
        ) {
          const mr = m.x + m.width;
          const mb = m.y + m.height;
          if (
            !(
              mr <= boss.x ||
              m.x >= boss.x + boss.width ||
              mb <= boss.y ||
              m.y >= boss.y + boss.height
            )
          ) {
            bossHit = true;
          }
        }
      }
    }

    if (asteroidIdx === -1 && enemyIdx === -1 && !bossHit) continue;

    // ── 2. Direct hit ────────────────────────────────────────────────────
    const impactX = m.x + m.width / 2;
    const impactY = m.y + m.height / 2;
    if (asteroidIdx !== -1) {
      const a = spaceState.asteroids[asteroidIdx];
      if (a.destructible) {
        const killed = a.takeDamage(SHIP_MISSILE_DIRECT_DAMAGE);
        if (killed) spawnExplosion(a.x + a.width / 2, a.y + a.height / 2);
      }
      // Indestructible asteroid still consumes the missile (matches the
      // laser absorb rule) — no damage dealt, AoE still applies.
    } else if (enemyIdx !== -1) {
      const e = spaceState.enemies[enemyIdx];
      const killed = e.takeDamage(SHIP_MISSILE_DIRECT_DAMAGE, impactX, impactY);
      if (killed) {
        const cx = e.x + e.width / 2;
        const cy = e.y + e.height / 2;
        spawnExplosion(cx, cy);
        maybeSpawnSpaceLoot(cx, cy);
      }
    } else if (bossHit) {
      const boss = spaceState.boss!;
      boss.takeDamage(SHIP_MISSILE_DIRECT_DAMAGE, impactX, impactY);
    }

    // ── 3. Splash ────────────────────────────────────────────────────────
    applySplash(
      impactX,
      impactY,
      SHIP_MISSILE_AOE_DAMAGE,
      asteroidIdx,
      enemyIdx,
      bossHit,
    );

    // ── 4. Detonate ──────────────────────────────────────────────────────
    spawnExplosion(impactX, impactY);
    triggerSpaceShake(
      SPACE_SHAKE_MAGNITUDE_MISSILE_PX,
      SPACE_SHAKE_DURATION_MISSILE_S,
    );
    m.dead = true;
    m.active = false;
  }
}
